import React, { useState, useEffect, useCallback } from "react";
import { Inbox, Send, RefreshCw, PenSquare, Loader2 } from "lucide-react";
import { getMailbox, syncMailbox, getMail, getMailAvailability } from "../../api";
import { useToast } from "../../hooks/use-toast";
import MailList from "./mailbox/MailList";
import MailView from "./mailbox/MailView";
import MailCompose from "./mailbox/MailCompose";
import MailAgentSettings from "./mailbox/MailAgentSettings";
import { log } from "../../lib/log";

const MailboxPanel = ({ isMaster }) => {
  const [folder, setFolder] = useState("inbox");
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [state, setState] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [mail, setMail] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [compose, setCompose] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const { toast } = useToast();

  const load = useCallback(async (f) => {
    try {
      const r = await getMailbox(f);
      setMessages(r.data.messages);
      setUnread(r.data.unread);
      setState(r.data.state || []);
    } catch (error) {
      log.error("Chargement de la boîte mail :", error);
      toast({ title: "Boîte mail indisponible", description: error?.response?.data?.detail || "Réessayez dans un instant.", variant: "destructive" });
    }
  }, [toast]);

  const loadAvailability = useCallback(
    () => getMailAvailability(10).then((r) => setAvailability(r.data)).catch((error) => log.error("Disponibilités :", error)),
    [],
  );

  useEffect(() => { load(folder); setSelectedId(null); setMail(null); }, [folder, load]);
  useEffect(() => { loadAvailability(); }, [loadAvailability]);

  useEffect(() => {
    if (!selectedId) return;
    getMail(selectedId)
      .then((r) => { setMail(r.data); load(folder); })
      .catch((error) => { log.error("Lecture de l'email :", error); setMail(null); });
  }, [selectedId, folder, load]);

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await syncMailbox();
      toast({ title: r.data.new ? `${r.data.new} nouvel(s) email(s)` : "Boîte à jour", description: "Réception relevée via IMAP." });
      await load(folder);
    } catch (err) {
      toast({ title: "Relève impossible", description: err?.response?.data?.detail || "Vérifiez les paramètres IMAP", variant: "destructive" });
    } finally { setSyncing(false); }
  };

  const onChanged = async (deleted = false) => {
    await load(folder);
    loadAvailability();
    if (deleted) { setSelectedId(null); setMail(null); return; }
    if (selectedId) getMail(selectedId).then((r) => setMail(r.data)).catch((error) => log.error("Lecture de l'email :", error));
  };

  const lastState = state.find((s) => s.account === "prospecting") || state[0];
  const FolderBtn = ({ id, icon: Icon, label, count }) => (
    <button onClick={() => setFolder(id)} data-testid={`mail-folder-${id}`}
      className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium w-full ${folder === id ? "bg-brand text-white" : "text-neutral-700 hover:bg-neutral-100"}`}>
      <Icon size={15} /> {label}
      {count > 0 && <span className={`ml-auto text-[11px] font-bold px-1.5 rounded-full ${folder === id ? "bg-white/20" : "bg-brand text-white"}`}>{count}</span>}
    </button>
  );

  return (
    <div data-testid="mailbox-panel">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="font-heading font-bold text-xl text-neutral-900">Boîte mail prospects</h2>
          <p className="text-xs text-neutral-500">
            prospect@ptitservices-pro.com · {lastState?.last_sync ? `relevée le ${new Date(lastState.last_sync).toLocaleString("fr-FR")}` : "jamais relevée"}
            {lastState?.last_error && <span className="text-red-600"> · erreur : {lastState.last_error}</span>}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={sync} disabled={syncing} data-testid="mail-sync-btn" className="flex items-center gap-1.5 text-sm text-neutral-600 hover:text-brand disabled:opacity-60">
            {syncing ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Actualiser
          </button>
          <button onClick={() => setCompose({})} data-testid="mail-compose-btn" className="flex items-center gap-2 bg-brand hover:bg-brand-dark text-white text-sm font-semibold px-4 py-2 rounded-md">
            <PenSquare size={16} /> Nouveau message
          </button>
        </div>
      </div>

      <div className="mb-5">
        <MailAgentSettings isMaster={isMaster} majorsCount={availability?.majors?.length || 0} onRan={() => onChanged()} />
      </div>

      <div className="grid lg:grid-cols-[minmax(0,380px)_1fr] gap-5">
        <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
          <div className="flex gap-1 p-2 border-b border-neutral-100">
            <FolderBtn id="inbox" icon={Inbox} label="Réception" count={unread} />
            <FolderBtn id="sent" icon={Send} label="Envoyés" />
          </div>
          <MailList messages={messages} folder={folder} selectedId={selectedId} onSelect={setSelectedId} />
        </div>
        <div>
          {mail ? (
            <MailView mail={mail} availability={availability} onChanged={onChanged}
              onReply={() => setCompose({ to: mail.from_email, subject: `Re: ${mail.subject}`, reply_to_id: mail.id, account: mail.account || "prospecting",
                body: `\n\n---- Message d'origine ----\n${(mail.body_text || "").split("\n").map((l) => `> ${l}`).join("\n")}` })} />
          ) : (
            <div className="h-full min-h-[240px] flex items-center justify-center text-sm text-neutral-400 border border-dashed border-neutral-200 rounded-xl" data-testid="mail-view-empty">
              Sélectionnez un email pour le lire, y répondre ou le confier à l'agent IA.
            </div>
          )}
        </div>
      </div>

      {compose && <MailCompose initial={compose} onClose={() => setCompose(null)} onSent={() => { setFolder("sent"); load("sent"); }} />}
    </div>
  );
};

export default MailboxPanel;
